import { Link } from 'react-router-dom'
import { BUSINESS } from '../../constants/business'

const QUICK_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About Us' },
  { href: '/reviews', label: 'Reviews' },
  { href: '/blog', label: 'Blog' },
]

const SERVICE_LINKS = [
  'Tire Sales & Installation',
  'Flat Tire Repair',
  'Tire Rotation',
  'Oil Change',
  'Brake Service',
  'Battery Replacement',
]

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-brand-charcoal text-brand-silver" role="contentinfo">
      {/* Main Footer */}
      <div className="container-custom py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Column 1: Logo + Tagline */}
        <div className="flex flex-col gap-4">
          <Link
            to="/"
            className="flex items-center rounded-lg px-2 py-1 w-fit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red"
            aria-label={`${BUSINESS.name} — Home`}
          >
            <img
              src="/photos/fasttrack-logo.png"
              alt={BUSINESS.name}
              className="h-10 w-auto object-contain"
            />
          </Link>
          <p className="text-sm leading-relaxed text-brand-silver/80">
            {BUSINESS.tagline}. Serving San Carlos and the Peninsula since {BUSINESS.yearEstablished}.
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-yellow-400 text-sm">★★★★★</span>
            <span className="text-sm font-semibold text-white">{BUSINESS.rating}</span>
            <span className="text-sm text-brand-silver/70">({BUSINESS.reviewCount} reviews)</span>
          </div>
          {/* Tire Brands */}
          <p className="text-xs text-brand-silver/60">
            Authorized dealer: {BUSINESS.tireBrands.join(' · ')}
          </p>
        </div>

        {/* Column 2: Quick Links */}
        <nav aria-label="Footer quick links">
          <h3 className="font-heading text-white text-base mb-4 uppercase tracking-wide">
            Quick Links
          </h3>
          <ul className="flex flex-col gap-2">
            {QUICK_LINKS.map(({ href, label }) => (
              <li key={href}>
                <Link
                  to={href}
                  className="text-sm text-brand-silver/80 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red rounded"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Column 3: Services */}
        <nav aria-label="Footer services">
          <h3 className="font-heading text-white text-base mb-4 uppercase tracking-wide">
            Our Services
          </h3>
          <ul className="flex flex-col gap-2">
            {SERVICE_LINKS.map((service) => (
              <li key={service}>
                <a
                  href={`tel:${BUSINESS.phone}`}
                  className="text-sm text-brand-silver/80 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red rounded"
                  aria-label={`${service} — call to book`}
                >
                  {service}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Column 4: Contact Info */}
        <div>
          <h3 className="font-heading text-white text-base mb-4 uppercase tracking-wide">
            Contact Us
          </h3>
          <div className="flex flex-col gap-4">
            {/* Phone */}
            <div className="flex items-start gap-3">
              <svg aria-hidden="true" className="mt-0.5 flex-shrink-0 text-brand-red" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.86 13a19.79 19.79 0 01-3.07-8.67A2 2 0 012.77 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
              <a
                href={`tel:${BUSINESS.phone}`}
                className="text-sm text-brand-silver/80 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red rounded"
              >
                {BUSINESS.phoneFormatted}
              </a>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <svg aria-hidden="true" className="mt-0.5 flex-shrink-0 text-brand-red" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <address className="not-italic text-sm text-brand-silver/80 leading-relaxed">
                <a
                  href={BUSINESS.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red rounded"
                  aria-label="Get directions to our shop"
                >
                  {BUSINESS.address.street}<br />
                  {BUSINESS.address.city}, {BUSINESS.address.state} {BUSINESS.address.zip}
                </a>
              </address>
            </div>

            {/* Hours */}
            <div className="flex items-start gap-3">
              <svg aria-hidden="true" className="mt-0.5 flex-shrink-0 text-brand-red" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <div className="text-sm text-brand-silver/80">
                <p className="font-semibold text-white">Open 7 Days a Week</p>
                <p>8:00 AM – 10:00 PM</p>
              </div>
            </div>

            {/* Maps Link */}
            <a
              href={BUSINESS.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-brand-red hover:text-brand-red/80 font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red rounded"
              aria-label="Open Google Maps directions"
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 11 22 2 13 21 11 13 3 11" />
              </svg>
              Get Directions
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-brand-silver/50">
          <p>
            &copy; {currentYear} {BUSINESS.name}. All rights reserved.
          </p>
          <p>
            Wheelchair accessible · On-site parking · Cards &amp; NFC payments accepted
          </p>
        </div>
      </div>
    </footer>
  )
}
